import { doctorBffApi, InvalidateArr } from '@/modules';
import { wrapError } from '../../error';
import { MedicalScoreService } from './medicalScore.service';
import { PatientId, WithPatientId } from '../surgicalEvent.slice';
import {
  MedicalScore,
  MedicalScoreDescription,
  MedicalScoreName
} from './model';
import { GroupedList } from '@/util';
import { SentryService } from '@ianesthesie-kit/sentry';
import { constructProtocolCacheKey, ProtocolName } from '@/modules/protocol';
import { ID } from '@ianesthesie-kit/services';

export type UpdateScoreArg = {
  score: Partial<MedicalScore>;
  finalize?: boolean;
};

export const medicalScoresApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    scoreNames: builder.query<
      MedicalScoreName[],
      { editable?: boolean } | undefined
    >({
      keepUnusedDataFor: Infinity,
      queryFn: (arg) =>
        MedicalScoreService.fetchScoreNames(arg?.editable).catch(wrapError)
    }),

    scoreDescription: builder.query<MedicalScoreDescription, MedicalScoreName>({
      keepUnusedDataFor: Infinity,
      queryFn: (name) => MedicalScoreService.describe(name).catch(wrapError)
    }),

    scores: builder.query<
      GroupedList<MedicalScore, MedicalScoreName>,
      PatientId
    >({
      queryFn: ({ patientId }) =>
        MedicalScoreService.getInstance(patientId)
          .fetchGrouped<MedicalScoreName, true>()
          .catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'Scores', id: patientId }
      ]
    }),

    scoresByName: builder.query<
      MedicalScore[],
      WithPatientId<{ name: MedicalScoreName }>
    >({
      queryFn: ({ patientId, name }) =>
        MedicalScoreService.getInstance(patientId)
          .fetch({ name })
          .catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'Scores', id: patientId }
      ]
    }),

    updateScore: builder.mutation<MedicalScore, WithPatientId<UpdateScoreArg>>({
      queryFn: ({ patientId, score, finalize = false }) =>
        MedicalScoreService.getInstance(patientId)
          .upsert(score, { finalize })
          .catch(wrapError),

      async onQueryStarted(
        { score: updatedScore, finalize, patientId },
        { dispatch, queryFulfilled }
      ) {
        // if score is to be finalized, do not perform any opt/pes updates
        if (finalize) {
          return;
        }

        const queryArg = { patientId, name: updatedScore.name! };
        // optimistic updates only for payload
        const optimisticPatchResult = dispatch(
          medicalScoresApi.util.updateQueryData(
            'scoresByName',
            queryArg,
            (scoresByName) => {
              let updatedScores: MedicalScore[] = scoresByName?.slice() ?? [];
              const index = updatedScores.findIndex((s) => !s.finalized);
              if (index < 0) {
                updatedScores = updatedScores.concat({
                  ...updatedScore,
                  finalized: false
                } as MedicalScore);
              } else {
                updatedScores[index] = {
                  ...updatedScores[index],
                  payload: updatedScore.payload
                } as MedicalScore;
              }

              Object.assign(scoresByName, updatedScores);
            }
          )
        );
        queryFulfilled
          // pessimistic updates for the whole entity
          .then(({ data: scoreEntity }) => {
            dispatch(
              medicalScoresApi.util.invalidateTags([
                { type: 'Scores', id: patientId }
              ])
            );
            dispatch(
              medicalScoresApi.util.updateQueryData(
                'scoresByName',
                queryArg,
                (scoresByName) => {
                  Object.assign(
                    scoresByName,
                    scoresByName?.map((sc) => (sc.finalized ? sc : scoreEntity))
                  );
                }
              )
            );
          })
          .catch((err) => {
            optimisticPatchResult.undo();
            SentryService.getInstance().captureException(err);
          });
      },

      invalidatesTags: (result, error, { patientId, finalize }) => {
        // draft scores will be updated optimistically
        if (error || !finalize) {
          return [];
        }

        const arr: InvalidateArr = [
          'Dashboard',
          { type: 'MedicalHo', id: patientId },
          { type: 'Scores', id: patientId }
        ];

        addRelevantProtocols(result, patientId, arr);

        return arr;
      }
    }),

    deleteScore: builder.mutation<unknown, WithPatientId<MedicalScore>>({
      queryFn: ({ patientId, ...score }) =>
        MedicalScoreService.getInstance(patientId)
          .delete(score)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId, ...score }) => {
        if (!result) {
          return [];
        }
        const arr: InvalidateArr = [
          'Dashboard',
          { type: 'Scores', id: patientId }
        ];

        if (score.finalized) {
          arr.push({ type: 'MedicalHo', id: patientId });
          addRelevantProtocols(score, patientId, arr);
        }
        return arr;
      }
    })
  }),
  overrideExisting: false
});

function addRelevantProtocols(
  score: MedicalScore | undefined,
  patientId: ID,
  arr: InvalidateArr
) {
  const name = score?.name;
  if (name === MedicalScoreName.STOP_BANG) {
    arr.push({
      type: 'Protocol',
      id: constructProtocolCacheKey({
        patientId,
        protocol: ProtocolName.SLEEP_APNEA
      })
    });
  } else if (
    name === MedicalScoreName.LEE ||
    name === MedicalScoreName.CARDIAC_RISK_FACTOR
  ) {
    arr.push({
      type: 'Protocol',
      id: constructProtocolCacheKey({
        patientId,
        protocol: ProtocolName.CARDIAC_STRESS_TEST
      })
    });
  } else if (name === MedicalScoreName.CPOP) {
    arr.push(
      {
        type: 'Protocol',
        id: constructProtocolCacheKey({
          patientId,
          protocol: ProtocolName.PREOP_CHRONIC_PAIN
        })
      },
      {
        type: 'Protocol',
        id: constructProtocolCacheKey({
          patientId,
          protocol: ProtocolName.POSTOP_CHRONIC_PAIN
        })
      }
    );
  } else if (name === MedicalScoreName.DN4) {
    arr.push(
      {
        type: 'Protocol',
        id: constructProtocolCacheKey({
          patientId,
          protocol: ProtocolName.POSTOP_CHRONIC_PAIN
        })
      },
      {
        type: 'PainPostopInfo',
        id: patientId
      }
    );
  }

  return arr;
}

export const {
  useScoresQuery: useGroupedScores,
  useScoreNamesQuery: useScoreNames,
  useScoreDescriptionQuery: useScoreDescription,
  useScoresByNameQuery,
  useUpdateScoreMutation,
  useDeleteScoreMutation
} = medicalScoresApi;
